import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSpring } from 'react-spring';
import { useMediaQuery } from 'react-responsive';
import { ArrowBackIcon } from '../../../assets/svg/SVGComponents';

import api from '../../../services/api';
import { useToast } from '../../../hooks/toast';

import {
  Content,
  HeaderMainContent,
  HeaderWrapper,
  MainContent,
  ProgressBarWrapper,
  ActivityTitle,
  DisplayChapter,
  ContentWrapper,
  ChapterTitle,
  NoContentContainer,
  ButtonWrapper,
  ShareButtonWrapper,
} from './styles';
import IRequestError from '../../../dtos/IRequestError';
import { useAuth } from '../../../hooks/auth';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import Popover from '../../../components/PopoverContainer/Popover';
import IChapterProps from '../../../dtos/IChapterProps';
import IStepProps from '../../../dtos/IStepsProps';
import IActivityProps from '../../../dtos/IActivitiesProps';
import SecondaryButton from '../../../components/Buttons/SecondaryButton';
import ITrailProps from '../../../dtos/ITrailsProps';
import ProgressBarTutorial from '../../../components/ProgressBarTutorial';
import MarkdownComponent from './MarkdownComponent';
import MakecodeFrame from './MakecodeFrame';
import SlideComponent from './SlideComponent';
import { ResourceType } from '../../../utils/enums';
import EmbeddingComponent from './EmbeddingComponent';
import LoadingState from './LoadingState';
import { useFeedback } from '../../../hooks/feedback';
import { ICreateFeedbackProps } from '../../../dtos/IFeedbacksProps';
import VideoComponent from './VideoComponent';
import SlideComponentMobile from './SlideComponentMobile';
import { useModal } from '../../../hooks/modal';
import ShareClassroom from '../../../modals/ShareClassroom';
import IClassroomsProps from '../../../dtos/IClassroomsProps';
import { TutorialId } from '../../../hooks/onboarding';
import SectionTutorialButton from '../../../components/Onboarding/SectionTutorialButton';

enum StepType {
  INTRO = 'intro',
  BUILDING = 'building',
  CIRCUIT = 'circuit',
  CODING = 'coding',
  BEHIND = 'behind-the-code',
  SIMULATOR = 'simulator',
  LEARN = 'what-did-you-learn',
}

interface IProgressBarProps {
  total_steps: number;
  steps: IProgressBarStepsProps[];
  progress: number;
  current_step: IProgressBarStepsProps;
}

interface IProgressBarStepsProps {
  progress: number;
  order: number;
  step: IStepProps;
}

const Tutorial: React.FC = () => {
  const { isSessionExpired, updateUser, user } = useAuth();
  const { handleModal } = useModal();
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { trail_slug, chapter_id, activity_slug } = useParams();
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const secondaryButtonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const [isChapterNext, setIsChapterNext] = useState(false);
  const [isChapterBack, setIsChapterBack] = useState(false);
  const [trailData, setTrailData] = useState<ITrailProps>();
  const [chapterData, setChapterData] = useState<IChapterProps>();
  const [currentActivity, setCurrentActivity] = useState<IActivityProps>();
  const [isLoading, setIsLoading] = useState(true);
  const [progressBarSteps, setProgressBarSteps] = useState<IProgressBarProps>();
  const [currentStep, setCurrentStep] = useState<IProgressBarStepsProps>();
  const { setOpenFeedback, handleFeedback } = useFeedback();
  const [showPopover, setShowPopover] = useState(false);
  const [openPopover, setOpenPopover] = useState(false);
  const activityCompletedRef = useRef<string | null>(null);
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });
  const isPortrait = useMediaQuery({ query: '(orientation: portrait)' });

  // Valores derivados via useMemo (antes eram state + useEffect desnecessários)
  const isMobile = useMemo(
    () => isTabletOrMobile || isPortrait,
    [isTabletOrMobile, isPortrait],
  );

  const stepsData = currentActivity?.steps;

  const currentStepData = useMemo(() => {
    if (!stepsData || !currentStep) return null;
    return stepsData.find(step => step.id === currentStep.step.id) ?? null;
  }, [stepsData, currentStep]);

  const backButtonStepDisabled = useMemo(
    () => !currentStep || currentStep.order <= 1,
    [currentStep],
  );

  const nextButtonStepDisabled = useMemo(
    () =>
      !currentStep ||
      !progressBarSteps ||
      currentStep.order >= progressBarSteps.steps.length,
    [currentStep, progressBarSteps],
  );

  const { backButtonActivityDisabled, nextButtonActivityDisabled } =
    useMemo(() => {
      if (!currentActivity || !trailData || !chapterData) {
        return {
          backButtonActivityDisabled: true,
          nextButtonActivityDisabled: true,
        };
      }

      const { activities } = chapterData;
      const { chapters } = trailData;

      const currentActivityIndex = activities.findIndex(
        activity => activity.id === currentActivity.id,
      );
      const currentChapterIndex = chapters.findIndex(
        chapter => chapter.id === chapterData.id,
      );

      return {
        backButtonActivityDisabled:
          currentActivityIndex <= 0 && currentChapterIndex <= 0,
        nextButtonActivityDisabled:
          currentActivityIndex >= activities.length - 1 &&
          currentChapterIndex >= chapters.length - 1,
      };
    }, [chapterData, currentActivity, trailData]);

  const [classroomData, setClassroomData] = useState<IClassroomsProps | null>(
    null,
  );

  const [isLoadingClassroomButton, setIsLoadingClassroomButton] =
    useState(false);

  useEffect(() => {
    api
      .get(`/chapters/${trail_slug}/${chapter_id}/${user.id}`)
      .then(response => {
        setTrailData(response.data.trail);
        setChapterData(response.data.chapters);
      })
      .catch(async (err: IRequestError) => {
        if (!(await isSessionExpired(err))) {
          addToast({
            type: 'error',
            expression: 'Ops!',
            description: t('toasts.errors.load_data_fail'),
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [addToast, t, user.id, chapter_id, trail_slug]);
  // nao coloque isSessionExpired no array de dependencias

  const handleNavigateIsActivityOrChapter = useCallback(() => {
    if (trailData && currentActivity && chapterData) {
      const { activities } = chapterData;

      const currentActivityIndex = activities.findIndex(
        activity => activity.id === currentActivity.id,
      );

      if (trailData.type === 'activity') {
        setIsChapterNext(false);
        setIsChapterBack(false);
        if (currentActivityIndex >= chapterData.activities.length - 1) {
          setIsChapterNext(true);
        }
        if (currentActivityIndex <= 0) {
          setIsChapterBack(true);
        }
      } else if (trailData.type === 'chapter') {
        setIsChapterNext(true);
        setIsChapterBack(true);
      }
    }
  }, [chapterData, currentActivity, trailData]);

  useEffect(() => {
    handleNavigateIsActivityOrChapter();
  }, [handleNavigateIsActivityOrChapter]);

  useEffect(() => {
    if (chapterData) {
      const activityData = chapterData.activities.find(
        activity => activity.slug === activity_slug,
      );
      if (activityData) {
        setCurrentActivity(activityData);
        // Resetar guard ao trocar de atividade
        activityCompletedRef.current = null;
        const feedbackData: ICreateFeedbackProps = {
          activity_id: activityData.id,
        };
        handleFeedback(feedbackData);
      }
    }
  }, [activity_slug, chapterData]);
  // não adicione handleFeedback nas dependências — causa loop de requisições

  // configurar barra de progresso
  useEffect(() => {
    if (!stepsData || stepsData.length === 0) {
      setProgressBarSteps(undefined);
      return;
    }

    const totalSteps = stepsData.length;
    const percentConclusion = totalSteps > 1 ? 100 / (totalSteps - 1) : 100;

    const steps: IProgressBarStepsProps[] = stepsData.map((step, index) => ({
      step,
      progress: percentConclusion * index,
      order: index + 1,
    }));

    const stepsBar: IProgressBarProps = {
      total_steps: totalSteps,
      steps,
      progress: 0,
      current_step: steps[0],
    };

    const introStep = steps.find(s => s.step.step_type === StepType.INTRO);
    setCurrentStep(introStep ?? steps[0]);
    setProgressBarSteps(stepsBar);
  }, [stepsData]);

  // mudar de step pelos botões
  const handleNavigateStep = useCallback(
    (direction: string) => {
      if (progressBarSteps) {
        const currentIndex = progressBarSteps.steps.findIndex(
          step => step.step === currentStep?.step,
        );
        const nextIndex =
          direction === 'next' ? currentIndex + 1 : currentIndex - 1;

        if (nextIndex >= 0 && nextIndex < progressBarSteps.steps.length) {
          setCurrentStep(progressBarSteps.steps[nextIndex]);
        }
      }
    },
    [currentStep, progressBarSteps],
  );

  const handleShareClassroom = useCallback(async () => {
    setIsLoadingClassroomButton(true);
    let xClassroomData: IClassroomsProps | null = null;
    if (currentActivity) {
      const localizedTitle = t(`activities.${currentActivity.slug}.title`, {
        defaultValue: currentActivity.title,
      });

      api
        .post('/classrooms/', {
          title: localizedTitle,
          activity_ids: [currentActivity.id],
        })
        .then(response => {
          xClassroomData = response.data.classroom;
          setClassroomData(response.data.classroom);
          setIsLoadingClassroomButton(false);
        })
        .catch(error => {
          console.error('Error fetching classroom data:', error);
          addToast({
            type: 'error',
            expression: 'Ops!',
            description:
              'Não foi possível criar a sala de aula. Tente novamente.',
          });
          setIsLoadingClassroomButton(false);
        })
        .finally(() => {
          handleModal(
            <ShareClassroom code={xClassroomData ? xClassroomData.code : ''} />,
          );
        });
    }
  }, [addToast, classroomData, currentActivity, handleModal, t]);

  const handleEndActivity = useCallback(async () => {
    if (!currentActivity) return;

    // Evitar envio duplicado para a mesma atividade
    if (activityCompletedRef.current === currentActivity.id) return;
    activityCompletedRef.current = currentActivity.id;

    const activity_id = currentActivity.id;
    const data = { activity_id };

    try {
      const response = await api.post(`/activities/user-progress`, data);
      updateUser(response.data);
    } catch (err) {
      // Permitir reenvio em caso de erro
      activityCompletedRef.current = null;
      if (!(await isSessionExpired(err as IRequestError))) {
        addToast({
          type: 'error',
          expression: 'Ops!',
          description: t('toasts.errors.update_progress_fail'),
        });
      }
    }
  }, [addToast, currentActivity, isSessionExpired, t, updateUser]);

  // useEffect para enviar finalização da atividade
  useEffect(() => {
    if (currentStep && progressBarSteps) {
      if (currentStep.order >= progressBarSteps.steps.length) {
        handleEndActivity();
      }
    }
  }, [currentStep, progressBarSteps]);

  const getAdjacentChapter = useCallback(
    (direction: 'next' | 'previous') => {
      if (!trailData || !chapterData) return null;

      const currentIndex = trailData.chapters.findIndex(
        ch => ch.id === chapterData.id,
      );
      if (currentIndex === -1) return null;

      const targetIndex =
        direction === 'next' ? currentIndex + 1 : currentIndex - 1;

      return trailData.chapters[targetIndex] ?? null;
    },
    [chapterData, trailData],
  );

  // mudar para próxima atividade ou próximo capitulo
  const handleNavigateButton = useCallback(
    (direction: string) => {
      if (trailData && chapterData && currentActivity && progressBarSteps) {
        const { activities } = chapterData;
        const { chapters } = trailData;

        const currentIndexActivity = activities.findIndex(
          activity => activity.id === currentActivity.id,
        );
        const currentIndexChapter = chapters.findIndex(
          chapter => chapter.id === chapterData.id,
        );

        const nextIndexActivity =
          direction === 'next'
            ? currentIndexActivity + 1
            : currentIndexActivity - 1;
        const nextIndexChapter =
          direction === 'next'
            ? currentIndexChapter + 1
            : currentIndexChapter - 1;
        if (trailData.type === 'activity') {
          if (
            nextIndexActivity >= 0 &&
            nextIndexActivity < chapterData.activities.length
          ) {
            setCurrentActivity(activities[nextIndexActivity]);
          } else if (isChapterNext) {
            const nextChapter = getAdjacentChapter('next');

            if (nextChapter) {
              setChapterData(nextChapter);
              setCurrentActivity(nextChapter.activities[0]);
            }
          } else if (isChapterBack) {
            const previousChapter = getAdjacentChapter('previous');

            if (previousChapter) {
              setChapterData(previousChapter);
              setCurrentActivity(previousChapter.activities[0]);
            }
          }
        }
        if (trailData.type === 'chapter') {
          if (
            nextIndexChapter >= 0 &&
            nextIndexChapter < trailData.chapters.length
          ) {
            const nextChapter = trailData.chapters[nextIndexChapter];

            if (nextChapter) {
              setChapterData(nextChapter);
              setCurrentActivity(nextChapter.activities[0]);
            }
          }
        }
        // Resetar guard ao navegar para outra atividade/capítulo
        activityCompletedRef.current = null;
        setCurrentStep(
          progressBarSteps.steps.find(
            step => step.step.step_type === StepType.INTRO,
          ),
        );
      }
    },
    [
      chapterData,
      currentActivity,
      getAdjacentChapter,
      isChapterBack,
      isChapterNext,
      progressBarSteps,
      trailData,
    ],
  );

  const handleFinalizeActivity = useCallback(async () => {
    if (currentActivity) {
      const activity_id = currentActivity.id;
      const data = { activity_id };
      api
        .post(`/activities/user-progress`, data)
        .then(async response => {
          updateUser(response.data);
        })
        .catch(async (err: IRequestError) => {
          if (!(await isSessionExpired(err))) {
            addToast({
              type: 'error',
              expression: 'Ops!',
              description: t('toasts.errors.update_progress_fail'),
            });
          }
        });
      navigate(`/trails/${trail_slug}/chapters`);
    }
  }, [
    addToast,
    currentActivity,
    isSessionExpired,
    navigate,
    t,
    trail_slug,
    updateUser,
  ]);
  // não atualize a o array de dependencias com o isSessionExpires

  useEffect(() => {
    if (chapterData && currentActivity && currentStep) {
      navigate(
        `/projects/${trail_slug}/${chapterData.id}/activity/${currentActivity.slug}`,
        { replace: true },
      );
    }
  }, [currentActivity, currentStep, navigate, trail_slug]);
  // não atualize com o chapter data;

  const handleAccesVideoButton = useCallback(() => {
    if (currentActivity) {
      if (currentActivity.resource_url != null) {
        navigate(currentActivity.resource_url);
      }
    }
  }, [currentActivity, navigate]);

  // fechar popover de feedback
  const handleClosePopover = useCallback(() => {
    setShowPopover(false);
    setOpenPopover(false);
    setOpenFeedback(false);
  }, [setOpenFeedback]);

  const feedbackPopUp = useSpring({
    transform: showPopover ? 'scale(1)' : 'scale(0)',
    config: { tension: 500 },
  });

  const handleStepClick = useCallback((step: IProgressBarStepsProps) => {
    setCurrentStep(step);
  }, []);

  return (
    <ContentWrapper>
      {!!isLoading && <LoadingState />}
      {!isLoading && (
        <Content>
          <HeaderWrapper>
            <Popover
              closePopover={handleClosePopover}
              style={feedbackPopUp}
              openPopover={openPopover}
            />
            <HeaderMainContent>
              {!isMobile && (
                <button type="button" ref={backButtonRef}>
                  <Link to={-1 as any}>
                    <ArrowBackIcon
                      width="1.22rem"
                      stroke="#1E88E5"
                      strokeWidth={2.5}
                      fill="none"
                    />
                    {t('navlinks.back')}
                  </Link>
                </button>
              )}

              <ChapterTitle>
                {!isLoading && chapterData && (
                  <DisplayChapter>
                    <h1>{t(`chapters.${chapterData.slug}.title`)}</h1>
                  </DisplayChapter>
                )}
                {!isLoading && currentActivity && (
                  <ActivityTitle>
                    <PrimaryButton
                      type="button"
                      disabled={backButtonActivityDisabled}
                      onClick={() => handleNavigateButton('back')}
                    >
                      {isChapterBack
                        ? t(`buttons.back_button_chapter`)
                        : t(`buttons.back_button_activity`)}
                    </PrimaryButton>
                    <div>
                      <h2>{t(`activities.${currentActivity.slug}.title`)}</h2>
                      <SectionTutorialButton
                        tutorialId={TutorialId.ACTIVITIES}
                      />
                    </div>
                    <PrimaryButton
                      type="button"
                      disabled={nextButtonActivityDisabled}
                      onClick={() => handleNavigateButton('next')}
                    >
                      {isChapterNext
                        ? t(`buttons.next_button_chapter`)
                        : t(`buttons.next_button_activity`)}
                    </PrimaryButton>
                  </ActivityTitle>
                )}
              </ChapterTitle>
              {!isMobile && (
                <SecondaryButton
                  onClick={handleFinalizeActivity}
                  type="button"
                  ref={secondaryButtonRef}
                >
                  {t('buttons.finalize_button')}
                </SecondaryButton>
              )}
            </HeaderMainContent>

            <ProgressBarWrapper id="tutorial-progress-header">
              {!!chapterData &&
                currentActivity &&
                currentStep &&
                progressBarSteps && (
                  <>
                    {!isMobile && (
                      <button
                        disabled={backButtonStepDisabled}
                        type="button"
                        onClick={() => handleNavigateStep('back')}
                      >
                        <ArrowBackIcon
                          width="1.22rem"
                          stroke="#1E88E5"
                          strokeWidth={2.5}
                          fill="none"
                        />
                        {t('buttons.back_button_progressbar')}
                      </button>
                    )}
                    <div>
                      <ProgressBarTutorial
                        total_steps={progressBarSteps.total_steps}
                        steps={progressBarSteps.steps}
                        current_step={currentStep}
                        onStepClick={handleStepClick}
                      />
                    </div>
                    {!isMobile && (
                      <button
                        type="button"
                        disabled={nextButtonStepDisabled}
                        onClick={() => handleNavigateStep('next')}
                      >
                        {t('buttons.next_button_progressbar')}
                        <ArrowBackIcon
                          width="1.22rem"
                          stroke="#1E88E5"
                          strokeWidth={2.5}
                          fill="none"
                          transform="scale(-1, 1)"
                        />
                      </button>
                    )}
                  </>
                )}
            </ProgressBarWrapper>

            <ShareButtonWrapper>
              <PrimaryButton
                onClick={() => handleShareClassroom()}
                type="button"
                style={{ marginBottom: '1rem' }}
                loading={isLoadingClassroomButton}
              >
                Compartilhar com os alunos
              </PrimaryButton>
            </ShareButtonWrapper>
          </HeaderWrapper>
          <MainContent id="tutorial-content-space">
            {!!chapterData && currentActivity && (
              <>
                {currentActivity.type === 'lesson' && (
                  <NoContentContainer>
                    <img
                      alt="404"
                      src="https://fuzzyplaydev.s3.us-east-2.amazonaws.com/image-404-gray.jpg"
                    />
                    <ButtonWrapper>
                      {currentActivity &&
                        currentActivity.resource_url != null && (
                          <PrimaryButton
                            type="button"
                            onClick={handleAccesVideoButton}
                          >
                            {t('buttons.access_video')}
                          </PrimaryButton>
                        )}
                    </ButtonWrapper>
                  </NoContentContainer>
                )}
                {currentStepData && (
                  <>
                    {currentStepData.resourse_type ===
                      ResourceType.MAKECODE && (
                      <MakecodeFrame
                        currentActivity={currentActivity}
                        currentStep={currentStepData}
                      />
                    )}

                    {currentStepData.resourse_type ===
                      ResourceType.MARKDOWN && (
                      <MarkdownComponent
                        currentActivity={currentActivity}
                        currentStep={currentStepData}
                      />
                    )}

                    {currentStepData.resourse_type === ResourceType.SLIDE &&
                      (!isMobile ? (
                        <SlideComponent
                          currentActivity={currentActivity}
                          currentStep={currentStepData}
                        />
                      ) : (
                        <SlideComponentMobile
                          currentActivity={currentActivity}
                          currentStep={currentStepData}
                        />
                      ))}
                    {currentStepData.resourse_type ===
                      ResourceType.EMBEDING && (
                      <EmbeddingComponent
                        currentActivity={currentActivity}
                        currentStep={currentStepData}
                      />
                    )}
                    {currentStepData.resourse_type === ResourceType.VIDEO && (
                      <VideoComponent
                        currentActivity={currentActivity}
                        currentStep={currentStepData}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </MainContent>
        </Content>
      )}
    </ContentWrapper>
  );
};
export default Tutorial;
